import { FC, ReactNode, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { ZIndex } from '../../constants'
import { breakpoints } from '../../theme/breakpoints'

const MOBILE_BREAKPOINT = breakpoints.mobile

export const defaultModalStyle: Modal.Styles = {
    overlay: {
        zIndex: ZIndex.Modal,
        background: 'rgba(0, 0, 0, 0.2)',
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        border: 'none',
        overflow: 'visible',
        padding: '0',
        background: 'unset',
        maxWidth: '80%',
        maxHeight: 'calc(100vh - 20vh)',
    },
}

export const mobileModalStyle: Modal.Styles = {
    overlay: {
        ...defaultModalStyle.overlay,
    },
    content: {
        ...defaultModalStyle.content,
        top: '50%',
        left: '16px',
        right: '16px',
        bottom: 'auto',
        transform: 'translateY(-50%)',
        maxWidth: 'none',
        width: 'auto',
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
    },
}

/** Shared id applied to the modal title heading (see BaseModal) so the
 *  dialog can be labelled via aria-labelledby. Only one modal is open at a
 *  time, so a stable id is safe. */
export const MODAL_TITLE_ID = 'tuhi-modal-title'

export interface GlobalModalProps {
    isOpen?: boolean
    style?: Modal.Styles
    children?: ReactNode
    /** Called when the user requests to close the modal (e.g. presses ESC). */
    onRequestClose?: () => void
}

export const GlobalModal: FC<GlobalModalProps> = ({
    isOpen,
    style,
    children,
    onRequestClose,
}) => {
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
        setIsMobile(mql.matches)

        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mql.addEventListener('change', handler)
        return () => mql.removeEventListener('change', handler)
    }, [])

    const responsiveStyle =
        style ?? (isMobile ? mobileModalStyle : defaultModalStyle)

    return (
        <Modal
            isOpen={isOpen ?? false}
            style={responsiveStyle}
            onRequestClose={onRequestClose}
            shouldCloseOnEsc
            // Forms can hold unsaved edits; don't dismiss on a stray backdrop
            // click. ESC and the explicit close button remain available.
            shouldCloseOnOverlayClick={false}
            shouldReturnFocusAfterClose
            aria={{ labelledby: MODAL_TITLE_ID, modal: true }}
            contentLabel="Dialog"
        >
            {children}
        </Modal>
    )
}
